import {
  LoginCred,
  useAuthGoogleGetAuthorizationMutation,
  useAuthLoginGoogleMutation,
  useAuthLoginMutation,
  useLazyAuthWhoamiQuery,
  useAuthSsoLoginMutation,
  useAuthSsoCallbackMutation,
} from '@app/services/auto-generated/auth'
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from '@common/components'
import {
  RECORDINGS_ROUTE,
  FORGET_ROUTE,
  MY_CLASSROOMS_ROUTE,
  PROFILE_ROUTE,
  VERIFY_ROUTE,
} from '@common/constants/routes'
import { useTranslation } from '@common/hooks'
import {
  CircularProgress,
  EmailIcon,
  GoogleIcon,
  KeyIcon,
  UserIcon,
  SchoolIcon,
} from '@common/icons'
import { yupResolver } from '@hookform/resolvers/yup'
import { SubmitHandler, useForm } from '@lib/form'
import { RouterLink, useNavigate, useSearchParams } from '@lib/router'
import { isErrorResponse } from '@types/errors'
import { isUNSW, urlEncodeEmail } from '@utils'
import React, { useState } from 'react'
import { loginSchema } from '@common/utils/validations/auth.validations'

const LoginForm: React.FC = () => {
  const [searchParams] = useSearchParams()
  const { t } = useTranslation('login-page')
  const navigate = useNavigate()

  const { handleSubmit, control, getValues } = useForm<LoginCred>({
    defaultValues: {
      password: '',
      email: '',
      username: '',
    },
    resolver: yupResolver(loginSchema),
  })

  const parseJSON = (str: string) => {
    try {
      return JSON.parse(str)
    } catch (err) {
      return null
    }
  }

  const [login, { isLoading: isLoadingLogin, isError }] = useAuthLoginMutation()
  const [showLoginForm, setShowLoginForm] = useState(false)
  const [loginWithSso, { isLoading: isLoadingLoginWithSso }] = useAuthSsoCallbackMutation()
  const [getUser] = useLazyAuthWhoamiQuery()
  const [getSsoAuth, { isLoading: isLoadingSsoAuth, isSuccess: isSuccessSsoLogin }] =
    useAuthSsoLoginMutation()

  const onSubmit: SubmitHandler<LoginCred> = async (formState) => {
    try {
      if (formState.email === '') {
        formState.email = null
      }
      if (formState.username === '') {
        formState.username = null
      }

      try {
        await login({ loginCred: formState }).unwrap()
      } catch (err: any) {
        if (isErrorResponse(err)) {
          if (err.data.detail.error_code === 'EXC_USER_NOT_VERIFIED') {
            const email: string | undefined = getValues('email')
            if (email) {
              navigate(`${VERIFY_ROUTE}?email=${urlEncodeEmail(email)}`)
            }
          }
        }
      }
      await getUser().unwrap()
    } catch (err) {}
    if (formState.username === null) {
      formState.username = ''
    }
    if (formState.email === null) {
      formState.email = ''
    }
  }

  const handleSSOLogin = async () => {
    try {
      const { url } = await getSsoAuth({
        ssoLoginRequest: {
          redirect_uri: `${window.location.origin}/`,
          state: state ? state : undefined,
        },
      }).unwrap()
      window.location.href = url
    } catch (error) {}
  }

  // for google sign in
  const code = searchParams.get('code')
  const state = searchParams.get('state')

  const loginSSOFlow = async () => {
    if (code) {
      await loginWithSso({
        ssoCallbackRequest: {
          code,
          state: state || '',
        },
      }).unwrap()
      await getUser().unwrap()
      const parsedState = parseJSON(decodeURIComponent(state))
      if (parsedState && parsedState['realRedirectUrl'] === PROFILE_ROUTE) {
        navigate(isUNSW() ? RECORDINGS_ROUTE : MY_CLASSROOMS_ROUTE)
        return
      }
      if (parsedState) {
        window.location.href = parsedState['realRedirectUrl']
      }
    }
  }

  React.useEffect(() => {
    if (code) {
      loginSSOFlow()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code])

  if (isLoadingSsoAuth || isLoadingLoginWithSso) {
    return (
      <Box
        sx={{
          width: '300px',
          height: '300px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Container
      component='form'
      maxWidth='xs'
      onSubmit={handleSubmit(onSubmit)}
      sx={{ minWidth: '444px', minHeight: '130px', display: 'flex', flexDirection: 'column' }}
    >
      {isError && (
        <Alert variant='outlined' severity='error' sx={{ mb: 2 }}>
          Please verify your email and password
        </Alert>
      )}
      <Stack spacing={2} justifyContent='center' sx={{ height: '100%', flex: 1 }}>
        <Typography variant='body1' align='center'>
          {t('subtitle', 'to continue to Mentor')}
        </Typography>
        <Button
          variant='outlined'
          fullWidth
          startIcon={<SchoolIcon />}
          onClick={handleSSOLogin}
          loading={isLoadingSsoAuth}
          sx={{ mt: 2 }}
        >
          {t('google-button.label', 'Login with SSO')}
        </Button>
        {!showLoginForm && (
          <Typography variant='body2'>
            <Link
              variant='body2'
              fontWeight='bold'
              sx={{ cursor: 'pointer' }}
              onClick={() => setShowLoginForm(true)}
            >
              Click here
            </Link>{' '}
            to login with Mentor ID instead
          </Typography>
        )}
      </Stack>
      {showLoginForm && (
        <>
          <Divider sx={{ my: 2 }}>{t('or', 'OR')}</Divider>
          <TextField
            name={'username'}
            type={'text'}
            control={control}
            placeholder={t('username-input.placeholder', 'Username')}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <UserIcon />
                </InputAdornment>
              ),
            }}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            name='password'
            type='password'
            control={control}
            placeholder={t('password-input.placeholder', 'Password')}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <KeyIcon />
                </InputAdornment>
              ),
            }}
            fullWidth
            sx={{ mb: 1 }}
          />
          {/* <Typography variant='body2' sx={{ mb: 2 }}>
            {t('forget-password.typo', 'Forgot your password?')}{' '}
            <Link
              underline='hover'
              component={RouterLink}
              to={`mailto:support@ispeak.to?subject=Forgot Password&body=I forgot my password, please assist.`}
            >
              {t('forget-password.link', 'Contact tech support')}
            </Link>
          </Typography> */}
          <Button
            loading={isLoadingLogin}
            variant='contained'
            type='submit'
            fullWidth
            sx={{ my: 2 }}
          >
            {t('submit-button.label', 'Login')}
          </Button>
        </>
      )}
    </Container>
  )
}

export default LoginForm
